import type { ApiClient } from "@hitit/api";
import type { Interval } from "date-fns";
import { ExcelWriter } from "../class";
import { format, formatISO, startOfDay, endOfDay, min } from "date-fns";

type Utils = {
    apiClient: ApiClient,
    t: (key: string) => string,
    setStatusMessage: (message: string) => void
}
type ReportRef = {
    range: Interval,
    storeId: string,
}

export async function writeSalesReportExcel(
    reportRef: ReportRef,
    utils: Utils
) {
    try {
        const { range, storeId } = reportRef;
        const { apiClient, t, setStatusMessage } = utils;
        const start = startOfDay(range.start);
        const end = min([endOfDay(range.end), new Date()]);
        setStatusMessage(t("hub.revenue.status_message.waiting_first_response"));
        //FirstResponse 
        const firstOrderListPromise = apiClient.listStoreOrders({
            storeid: storeId,
            currentPage: 0,
            createdDateTimeStartUTC: start.toISOString(),
            createdDateTimeEndUTC: end.toISOString(),
        });

        const salesPromise = apiClient.getByPayment({
            startTimeInTimeZone: formatISO(start),
            endTimeInTimeZone: formatISO(end),
            reportTimeZone: "ASIA_SEOUL",
            reportResolution: "YEARLY",
            storeid: storeId,
        });

        const reportItemPromise = apiClient.getStoreSalesReportStoreItems({
            startTimeInTimeZone: formatISO(start),
            endTimeInTimeZone: formatISO(end),
            reportTimeZone: "ASIA_SEOUL",
            reportResolution: "MONTHLY",
            storeid: storeId,
        });
        const pointInitPromise = apiClient.getRewardFromStore(storeId, 0, start.toISOString(), end.toISOString())

        const [firstOrderListResponse, salesResponse, reportItemResponse, firstPointResponse] = await Promise.all([firstOrderListPromise, salesPromise, reportItemPromise, pointInitPromise])
        const orderListTotalPages = firstOrderListResponse.data.totalPages;
        const pointTotalpages = firstPointResponse.data.totalPages ? firstPointResponse.data.totalPages : 1;
        setStatusMessage(t("hub.revenue.status_message.first_response"));
        //await Pages
        const orderListPromiseLength = (orderListTotalPages - 1) < 0 ? 0 : (orderListTotalPages - 1)


        const orderListPromises = new Array(orderListPromiseLength).fill(null).map((_, i) => {
            return () => apiClient.listStoreOrders({
                storeid: storeId,
                currentPage: i + 1,
                createdDateTimeStartUTC: start.toISOString(),
                createdDateTimeEndUTC: end.toISOString(),
            })
        })
        const pointListPromiseLength = (pointTotalpages - 1) < 0 ? 0 : (pointTotalpages - 1)
        const pointListPromises = new Array(pointListPromiseLength).fill(null).map((_, index) => {
            return () => apiClient.getRewardFromStore(
                storeId,
                index + 1,
                start.toISOString(),
                end.toISOString()
            )
        });

        let orderListResponses: Awaited<ReturnType<typeof apiClient.listStoreOrders>>[] = [firstOrderListResponse];
        let pointListResponses: Awaited<ReturnType<typeof apiClient.getRewardFromStore>>[] = [firstPointResponse];

        for (let i = 0; i < orderListPromises.length; i += 2) {
            const slice = orderListPromises.slice(i, i + 2).map((v) => v());
            const responses = await Promise.all([...slice]);
            orderListResponses = [...orderListResponses, ...responses];
            setStatusMessage(`
                ${t("hub.revenue.xlsx.orders")} : ${orderListResponses.length}/${orderListPromises.length + 1}
                <br>
                ${t("hub.point.title")} : ${pointListResponses.length}/${pointListPromises.length + 1}
            `);

        }

        for (let i = 0; i < pointListPromises.length; i += 2) {
            const slice = pointListPromises.slice(i, i + 2).map((v) => v());
            const responses = await Promise.all([...slice]);
            pointListResponses = [...pointListResponses, ...responses];
            setStatusMessage(`
                ${t("hub.revenue.xlsx.orders")} : ${orderListResponses.length}/${orderListPromises.length + 1}
                <br>
                ${t("hub.point.title")} : ${pointListResponses.length}/${pointListPromises.length + 1}
            `);
        }


        //Data
        const pointDateFormat = (date: Date) => {
            const month = date.getMonth() < 9 ? "0" + (date.getMonth() + 1) : (date.getMonth() + 1);
            const __date = date.getDate() < 10 ? "0" + date.getDate() : date.getDate()
            const yyyyMMdd = date.getFullYear() + "-" + month + "-" + __date
            const hhmm = date.getHours() + ":" + date.getMinutes();

            return yyyyMMdd + " " + hhmm
        }

        const rewardData = pointListResponses.map(v => v.data.rewardMinDTOs)
        const orderData = orderListResponses
            .map((response) => response.data.orderMinDTOs)
            .reduce((acc, val) => acc.concat(val), []);
        const salesData = salesResponse.data;
        const salesItemsData = reportItemResponse.data;
        const itemMap: Record<string, number> = {};
        const card = {
            tax: 0,
            totalPriceBeforeTax: 0,
            total: 0,
        };

        const cash = {
            tax: 0,
            totalPriceBeforeTax: 0,
            total: 0,
        };

        const pointHistory = [
            ...rewardData.reduce((prev, cur) => {
                return [...prev, ...cur]
            }, []).map((v) => {
                return [
                    t(`global.point.rewardStatus.${v.rewardStatus}`),
                    v.userIdentity,
                    v.summary,
                    v.reward,
                    pointDateFormat(new Date(v.createdDate + "Z"))
                ]
            })
        ]

        const totalUsedpoint = rewardData.reduce((prev, cur) => {
            const usedPoint = cur.reduce((prev, cur) => cur.rewardStatus === "REDEEMED" && cur as any["cancelDate"] as string ? prev + cur.reward : prev, 0);
            return prev + usedPoint
        }, 0)

        const totalEarnedpoint = rewardData.reduce((prev, cur) => {
            const totalEarendPoint = cur.reduce((prev, cur) => cur.rewardStatus === "EARNED" && cur as any["cancelDate"] as string ? prev + cur.reward : prev, 0);
            return prev + totalEarendPoint
        }, 0)

        let totalCount = 0;

        for (let report of salesData.salesReportsByPaymentMethod) {
            totalCount += report.count;

            if (report.paymentMethod === "INSTORE_CAT") {
                card.total += report.sum;
            }

            if (report.paymentMethod === "INSTORE_CASH") {
                cash.total += report.sum;
            }
        }

        for (const report of salesItemsData.salesReportStoreItems) {
            if (!report.itemname) continue;
            if (itemMap[report.itemname] === undefined) itemMap[report.itemname] = 0;
            itemMap[report.itemname] += report.count;
        }



        //ExcelData 
        const summary = [
            [t("hub.revenue.xlsx.total_transaction_count"), totalCount],
            [],
            [
                t("hub.revenue.xlsx.payment_type"),
                t("hub.revenue.xlsx.total"),
            ],
            [],
            [t("hub.revenue.xlsx.card"), card.total],
            [t("hub.revenue.xlsx.cash"), cash.total],
            ["총", cash.total + card.total],
        ]
        const items = Object.entries(itemMap).sort((a, b) => b[1] - a[1]);
        const ordersOpt = { dateNF: 'yyyy"-"mm"-"dd" "hh":"mm":"ss' };
        const orders = [
            [
                t("hub.order.order_status"),
                t("hub.order.order_number"),
                t("hub.order.order_contents"),
                t("hub.revenue.xlsx.payment_type"),
                t("hub.order.total"),
                t("hub.order.order_date"),
            ],
            ...orderData.map((order) => [
                t(`hub.order.status.${order.orderStatus}`),
                order.shortid,
                order.summary,
                t(`hub.order.payment_type.${order.orderPaymentMethod || "UNKNWON"}`),
                order.grandTotalPrice,
                new Date(Date.parse(order.createdDate + "Z")),
            ]),
        ]
        const ordersCols = [
            { wch: 13 },
            { wch: 10 },
            { wch: 50 },
            { wch: 10 },
            { wch: 20 },
            { wch: 30 },
        ];
        const sheetOption = {
            colWidth: ordersCols,
            sheetOpts: ordersOpt
        }
        const reward = [
            [
                t('global.point.pointColumn.type'),
                t('global.point.pointColumn.phone'),
                t('global.point.pointColumn.orders'),
                t('global.point.pointColumn.amount'),
                t('global.point.pointColumn.date')
            ],
            ...pointHistory,
            [],
            [
                t('global.point.newPoint'),
                totalEarnedpoint
            ],
            [
                t('global.point.usedPoint'),
                totalUsedpoint
            ]
        ]


        //Excels 
        const s = format(range.start, "yyyy-MM-dd");
        const e = format(range.end, "yyyy-MM-dd");
        const excel = ExcelWriter.NewExcel();
        const summarySheet = ExcelWriter.NewSheet(summary)
        const itemSheet = ExcelWriter.NewSheet(items)
        const orderSheet = ExcelWriter.NewSheet(orders, undefined, sheetOption)
        const pointSheet = ExcelWriter.NewSheet(reward, undefined, sheetOption)


        const file = ExcelWriter.ConcatSheetToExcel(excel, [
            { excelSheet: summarySheet, name: t("hub.revenue.xlsx.summary") },
            { excelSheet: itemSheet, name: t("hub.revenue.sold_items") },
            { excelSheet: orderSheet, name: t("hub.revenue.xlsx.orders") },
            { excelSheet: pointSheet, name: "Point" }
        ])
        ExcelWriter.writeExcel(file, `${s}_${e}.xlsx`);
        setStatusMessage("DONE")
    } catch (e) {
        throw e
    }
}

